"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_SEO_CHECK_SEVERITY = exports.APP_SEO_CHECK_CODES = exports.SEO_DESCRIPTION_MAX_LENGTH = exports.SEO_TITLE_MAX_LENGTH = void 0;
exports.evaluateAppSeoCheck = evaluateAppSeoCheck;
/**
 * Rule-of-thumb display budgets. Search engines cut by pixel width and have
 * no fixed character limit: the container SEO settings enforce the budgets on
 * save, the check only tips on longer texts that come from content rows.
 */
exports.SEO_TITLE_MAX_LENGTH = 60;
exports.SEO_DESCRIPTION_MAX_LENGTH = 160;
/** Ordered by weight: findings are reported in this order. */
exports.APP_SEO_CHECK_CODES = [
    'duplicate_title',
    'locale_missing',
    'description_missing',
    'duplicate_description',
    'ai_crawlers_blocked',
    'title_too_long',
    'description_too_long',
    'image_missing',
];
exports.APP_SEO_CHECK_SEVERITY = {
    duplicate_title: 'red',
    locale_missing: 'red',
    description_missing: 'yellow',
    duplicate_description: 'yellow',
    ai_crawlers_blocked: 'yellow',
    title_too_long: 'tip',
    description_too_long: 'tip',
    image_missing: 'tip',
};
function evaluateAppSeoCheck(input) {
    return input.groups.map(group => {
        const pageIds = new Set(group.pageIds);
        const routes = input.routes.filter(route => pageIds.has(route.pageId) && !route.noindex);
        if (group.never || !group.indexed || routes.length === 0) {
            return {
                key: group.key,
                title: group.title,
                status: 'off',
                findings: [],
            };
        }
        const findings = collectFindings(routes, input);
        return {
            key: group.key,
            title: group.title,
            status: findings.some(finding => finding.severity === 'red')
                ? 'red'
                : findings.some(finding => finding.severity === 'yellow')
                    ? 'yellow'
                    : 'green',
            findings,
        };
    });
}
function collectFindings(routes, input) {
    const counts = new Map();
    const add = (code, count = 1) => {
        if (count > 0)
            counts.set(code, (counts.get(code) ?? 0) + count);
    };
    const titles = new Map();
    const descriptions = new Map();
    for (const route of routes) {
        const description = route.description?.trim() ?? '';
        if (!description)
            add('description_missing');
        if (description.length > exports.SEO_DESCRIPTION_MAX_LENGTH) {
            add('description_too_long');
        }
        if (route.title.trim().length > exports.SEO_TITLE_MAX_LENGTH) {
            add('title_too_long');
        }
        if (!route.ogImage)
            add('image_missing');
        countText(titles, route.locale, route.title);
        if (description)
            countText(descriptions, route.locale, description);
    }
    for (const count of titles.values()) {
        if (count > 1)
            add('duplicate_title', count);
    }
    for (const count of descriptions.values()) {
        if (count > 1)
            add('duplicate_description', count);
    }
    // A locale without its own container text shows the default locale's text,
    // i.e. another language. Without a default text there is nothing to borrow.
    const { titlesI18n, descriptionsI18n } = input.containerSeo;
    add('locale_missing', input.availableLocales.filter(locale => locale !== input.defaultLocale &&
        (borrowsDefaultText(titlesI18n, locale, input.defaultLocale) ||
            borrowsDefaultText(descriptionsI18n, locale, input.defaultLocale))).length);
    if (input.aiCrawlerAccess !== 'allowed')
        add('ai_crawlers_blocked');
    return exports.APP_SEO_CHECK_CODES.flatMap(code => {
        const count = counts.get(code);
        return count
            ? [{ code, severity: exports.APP_SEO_CHECK_SEVERITY[code], count }]
            : [];
    });
}
function countText(counts, locale, text) {
    const key = `${locale}\u0000${text.trim().toLowerCase()}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
}
function borrowsDefaultText(texts, locale, defaultLocale) {
    return hasText(texts?.[defaultLocale]) && !hasText(texts?.[locale]);
}
function hasText(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
